/**
 * Yield metrics for a generation parcel test fit.
 *
 * Pure projection over the solver result — nothing here influences placement,
 * so it can evolve freely without touching frozen-exhibit re-validation.
 * "Green reserve" is the parcel area not consumed by placed components; it is
 * reported for context only and never drops blocks (fleet sizing owns the
 * block count; shortfalls surface as overflow/containment findings).
 */
import polygonClipping from 'polygon-clipping';

import type { GenerationParcelTestFitResult } from './generationLayoutGeometry';
import {
  closedRingM,
  multiPolygonAreaM2,
  SQUARE_METRES_PER_ACRE,
} from './generationLayoutUsableArea';

const CONTAINED_TOLERANCE_ACRES = 0.01;

/**
 * Runtime-compatible ground ring. Current solver results carry polygonM, but
 * older frozen/test-fit payloads used only the rectangle centre and extents.
 */
export function generationComponentGroundRingM(
  component: GenerationParcelTestFitResult['components'][number],
): Array<{ xM: number; yM: number }> {
  const polygon = component.rectangle.polygonM as
    | readonly { xM: number; yM: number }[]
    | undefined;
  if (polygon && polygon.length >= 3) {
    const ring = polygon.map(point => ({ xM: point.xM, yM: point.yM }));
    const first = ring[0]!;
    const last = ring.at(-1)!;
    if (first.xM !== last.xM || first.yM !== last.yM) ring.push({ ...first });
    return ring;
  }
  const { xM, yM, widthM, heightM } = component.rectangle;
  if (![xM, yM, widthM, heightM].every(Number.isFinite) || widthM <= 0 || heightM <= 0) {
    return [];
  }
  const halfWidth = widthM / 2;
  const halfHeight = heightM / 2;
  return [
    { xM: xM - halfWidth, yM: yM - halfHeight },
    { xM: xM + halfWidth, yM: yM - halfHeight },
    { xM: xM + halfWidth, yM: yM + halfHeight },
    { xM: xM - halfWidth, yM: yM + halfHeight },
    { xM: xM - halfWidth, yM: yM - halfHeight },
  ];
}

export interface GenerationLayoutYieldSummary {
  /** Generation blocks placed by the packer (always the full fleet). */
  blocksPlaced: number;
  /** Blocks fully on buildable ground: inside the parcel, meeting setback,
   * clear of exclusion zones. */
  blocksContained: number;
  /** Placed component area over the post-setback packing envelope. */
  areaUtilizationPct: number | null;
  /** Parcel area left free of components, as a share of the parcel. */
  greenReservePct: number | null;
  parcelAcres: number | null;
  componentAcres: number | null;
  excludedAcres: number | null;
  /** Scheme labels evaluated by the packer, selected first. */
  schemesCompared: readonly string[];
}

/**
 * Physical ground occupied inside the target parcel.
 *
 * Components can overlap after manual pinning/import. Summing their declared
 * acreages double-counts those intersections, so utilization/headroom use one
 * deterministic union clipped to the parcel. A clipping failure is unknown,
 * never silently replaced by a reassuring number.
 */
export function occupiedGenerationGroundAcres(
  testFit: GenerationParcelTestFitResult,
): number | null {
  if (testFit.components.length === 0) return testFit.plan ? 0 : null;
  try {
    let occupied: polygonClipping.MultiPolygon = [];
    for (const component of testFit.components) {
      const ring = closedRingM(generationComponentGroundRingM(component));
      if (ring.length < 4) continue;
      const polygon = [ring] as polygonClipping.Polygon;
      occupied = occupied.length === 0
        ? polygonClipping.union(polygon)
        : polygonClipping.union(occupied, polygon);
    }
    if (occupied.length === 0) return 0;
    const parcelRing = testFit.envelope?.parcelPolygonM;
    if (parcelRing && parcelRing.length >= 3) {
      occupied = polygonClipping.intersection(
        occupied,
        [closedRingM(parcelRing)] as polygonClipping.Polygon,
      );
    }
    return multiPolygonAreaM2(occupied) / SQUARE_METRES_PER_ACRE;
  } catch {
    return null;
  }
}

export function summarizeLayoutYield(
  testFit: GenerationParcelTestFitResult,
): GenerationLayoutYieldSummary {
  const plan = testFit.plan;
  const blocks = testFit.components.filter(
    component => component.rectangle.kind === 'generation-block',
  );
  const blocksContained = blocks.filter(component => (
    (component.outsideAcres ?? Number.POSITIVE_INFINITY) <= CONTAINED_TOLERANCE_ACRES
    && component.meetsParcelEdgeSetback !== false
    && (component.exclusionOverlapAcres ?? 0) <= CONTAINED_TOLERANCE_ACRES
  )).length;

  const parcelAcres = testFit.usable?.parcelAcres ?? testFit.parcel?.area ?? null;
  // Includes generated, manually pinned and custom geometry without counting
  // overlapping ground twice.
  const componentAcres = plan ? occupiedGenerationGroundAcres(testFit) : null;
  const greenReservePct = parcelAcres != null && parcelAcres > 0 && componentAcres != null
    ? Math.max(0, (parcelAcres - componentAcres) / parcelAcres * 100)
    : null;

  const selectedScheme = plan?.packingScheme;
  const schemesCompared = plan?.schemeEvaluations
    ? [...new Set([
        ...(selectedScheme ? [selectedScheme] : []),
        ...plan.schemeEvaluations.map(candidate => candidate.scheme),
      ])]
    : [];

  return {
    blocksPlaced: blocks.length,
    blocksContained,
    areaUtilizationPct: plan && componentAcres != null && plan.packingEnvelope.areaAcres > 0
      ? componentAcres / plan.packingEnvelope.areaAcres * 100
      : null,
    greenReservePct,
    parcelAcres,
    componentAcres,
    excludedAcres: testFit.usable?.excludedAcres ?? null,
    schemesCompared,
  };
}
